import {
 useState, useEffect, useCallback, useRef
} from "react";
import {
 ArrowTopRightOnSquareIcon
} from "@heroicons/react/24/outline";
import {
 zoomService
} from "../services/api";
import type {
 ZoomOAuthInitResponse
} from "../services/api";
import {
 OnboardingEvents,
 trackEvent
} from '../analytics/posthog';
import {
  OAuthWaitingState
} from "./pairing/OAuthWaitingState";
import {
  PairingModalShell
} from "./pairing/PairingModalShell";

interface ZoomPairingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ZoomPairingModal({
  isOpen,
  onClose,
  onSuccess,
}: ZoomPairingModalProps) {
  const [oauthData, setOauthData] = useState<ZoomOAuthInitResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollingForCallback, setPollingForCallback] = useState(false);
  const modalOpenTracked = useRef(false);

  // Define callbacks first so they can be used in useEffects
  const handleClose = useCallback(() => {
    trackEvent(OnboardingEvents.ZOOM_MODAL_CLOSED);
    setOauthData(null);
    setError(null);
    setPollingForCallback(false);
    onClose();
  }, [onClose]);

  // Mirrors handleClose's cleanup but emits ZOOM_CONNECTED instead of
  // ZOOM_MODAL_CLOSED, so a successful connect isn't also counted as a dismiss.
  const handleSuccess = useCallback(() => {
    trackEvent(OnboardingEvents.ZOOM_CONNECTED);
    setOauthData(null);
    setError(null);
    setPollingForCallback(false);
    onClose();
    onSuccess();
  }, [onClose, onSuccess]);

  const initiateZoomOAuth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await zoomService.initiateOAuth();
      setOauthData(data);
    } catch (err: any) {
      const message = err.response?.data?.detail || "Failed to initiate Zoom OAuth";
      trackEvent(OnboardingEvents.ZOOM_CONNECTION_FAILED, { error: message });
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && !oauthData) {
      initiateZoomOAuth();
    }
  }, [isOpen, oauthData, initiateZoomOAuth]);

  useEffect(() => {
    if (isOpen && !modalOpenTracked.current) {
      trackEvent(OnboardingEvents.ZOOM_MODAL_OPENED);
      modalOpenTracked.current = true;
    }
    if (!isOpen) {
      modalOpenTracked.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    // Set up callback handler for OAuth redirect
    const handleCallback = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === "ZOOM_OAUTH_SUCCESS") {
        setPollingForCallback(false);
        handleSuccess();
      } else if (event.data.type === "ZOOM_OAUTH_ERROR") {
        setPollingForCallback(false);
        const message = event.data.error || "OAuth authentication failed";
        trackEvent(OnboardingEvents.ZOOM_CONNECTION_FAILED, { error: message });
        setError(message);
      }
    };

    window.addEventListener("message", handleCallback);
    return () => window.removeEventListener("message", handleCallback);
  }, [handleSuccess]);

  const checkConnectionStatus = useCallback(async () => {
    try {
      const status = await zoomService.getStatus();
      if (status.connected) {
        handleSuccess();
      }
    } catch (err) {
      console.error("Failed to check Zoom status:", err);
    }
  }, [handleSuccess]);

  const openZoomOAuth = useCallback(() => {
    if (!oauthData?.oauth_url) return;

    trackEvent(OnboardingEvents.ZOOM_OAUTH_STARTED);

    // Open OAuth URL in a popup window
    const popup = window.open(
      oauthData.oauth_url,
      "zoom-oauth",
      "width=600,height=700,scrollbars=yes,resizable=yes"
    );

    setPollingForCallback(true);

    // Poll for popup closure or success
    const pollTimer = setInterval(() => {
      if (popup?.closed) {
        clearInterval(pollTimer);
        setPollingForCallback(false);
        // Check if the connection was successful
        checkConnectionStatus();
      }
    }, 1000);

    // Clean up after 5 minutes
    setTimeout(() => {
      clearInterval(pollTimer);
      if (popup && !popup.closed) {
        popup.close();
      }
      setPollingForCallback(false);
    }, 300000);
  }, [oauthData?.oauth_url, checkConnectionStatus]);

  return (
    <PairingModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title="Connect Zoom Account"
    >
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">
            Setting up Zoom connection...
          </p>
        </div>
      ) : pollingForCallback ? (
        <OAuthWaitingState iconBgClass="bg-blue-100" iconColorClass="text-blue-600" />
      ) : (
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            <p className="mb-3">
              Connect your Zoom account to enable meeting scheduling
              and management through your assistant.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <h4 className="font-medium text-blue-900 mb-2">
                What you'll get:
              </h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Create Zoom meetings automatically</li>
                <li>• View meeting details and attendance</li>
                <li>• Track meeting duration and timing</li>
                <li>• Receive meeting notifications</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={openZoomOAuth}
              disabled={!oauthData?.oauth_url}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors flex items-center space-x-2"
            >
              <span>Connect to Zoom</span>
              <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </PairingModalShell>
  );
}
